<template>
  <v-container>
    <div v-for="(question, index) in questionsCopy" key="index" class="mt-3">
      <v-form
          validate-on="input"
          :ref="(el) => (formRef[question.id] = el)"
          v-model="formIsValid[question.id]">
        <condition-debug-all
            v-if="showHiddenQuestions || question.visibility"
            :conditions="question?.conditions"
            :item-id="question?.id"
        />
        <div
          v-if="question.data_type?.value === QST.INPUT.TYPE.TEXT"
          :class="{
            'text-disabled pointer-none': !question.visibility || presenceIsFalse,
            'd-none': !showHiddenQuestions && !question.visibility
          }"
        >
          <div class="text-body-1 font-weight-bold">
            {{ question.label }} {{ question.mandatory && '*' || '' }}
            <v-progress-circular
                v-if="questionLoadingState[index]"
                size="x-small"
                indeterminate
            />
            <history-value
                :question="question"
                :missionReadingHistory="missionReadingHistory"
                :attachment-type="props.attachmentType"
                :attachment-id="props.attachmentId" />
          </div>

          <v-text-field
            v-model="formData[question.id]"
            type="text"
            density="compact"
            single-line
            :rules="inputRules(question)"
            :return-object="true"
            @update:model-value="() => {
              questionLoadingState[index] = true;
              formIsValid[question.id] = null;
              debounceSubmit(index, question.id)
            }"
          />
        </div>

        <div
          v-if="question.data_type?.value === QST.INPUT.TYPE.NUMERIC"
          :class="{
            'text-disabled pointer-none': !question.visibility || presenceIsFalse,
            'd-none': !showHiddenQuestions && !question.visibility
          }"
        >
          <div class="text-body-1 font-weight-bold">
            {{ question.label }} {{ question.mandatory && '*' || '' }}
            <v-progress-circular
                v-if="questionLoadingState[index]"
                size="x-small"
                indeterminate
            />
            <history-value
                :question="question"
                :missionReadingHistory="missionReadingHistory"
                :attachment-type="props.attachmentType"
                :attachment-id="props.attachmentId" />
          </div>

          <v-text-field
            v-model="formData[question.id]"
            type="number"
            v-bind="getQuestionInformations(question, formData[question.id])"
            density="compact"
            single-line
            :rules="inputRules(question)"
            :return-object="true"
            @update:model-value="() => {
              questionLoadingState[index] = true;
              formIsValid[question.id] = null;
              debounceSubmit(index, question.id)
            }"
          />
        </div>

        <div
          v-if="question.data_type?.value === QST.INPUT.TYPE.TEXTAREA"
          :class="{
            'text-disabled pointer-none': !question.visibility || presenceIsFalse,
            'd-none': !showHiddenQuestions && !question.visibility
          }"
        >
          <div class="text-body-1 font-weight-bold">
            {{ question.label }} {{ question.mandatory && '*' || '' }}
            <v-progress-circular
                v-if="questionLoadingState[index]"
                size="x-small"
                indeterminate
            />
            <history-value
                :question="question"
                :missionReadingHistory="missionReadingHistory"
                :attachment-type="props.attachmentType"
                :attachment-id="props.attachmentId" />
          </div>

          <v-textarea
            v-model="formData[question.id]"
            density="compact"
            single-line
            :rules="inputRules(question)"
            :return-object="true"
            @update:model-value="() => {
              questionLoadingState[index] = true;
              formIsValid[question.id] = null;
              debounceSubmit(index, question.id)
            }"
          />
        </div>

        <div
          v-if="question.data_type?.value === QST.INPUT.TYPE.YES_NO"
          :class="{
            'text-disabled pointer-none': !question.visibility || presenceIsFalse,
            'd-none': !showHiddenQuestions && !question.visibility
          }"
        >
          <div class="text-body-1 font-weight-bold">
            {{ question.label }} {{ question.mandatory && '*' || '' }}
            <v-progress-circular
                v-if="questionLoadingState[index]"
                size="x-small"
                indeterminate
            />
            <history-value
                :question="question"
                :missionReadingHistory="missionReadingHistory"
                :attachment-type="props.attachmentType"
                :attachment-id="props.attachmentId" />
          </div>

          <yes-no-toggle-input-old
              :ref="(el) => (yesNoInput[question.id] = el)"
            v-model="formData[question.id]"
            :rules="inputRules(question)"
            @update:model-value="() => {
              questionLoadingState[index] = true;
              formIsValid[question.id] = null;
              checkAndUpdateFormValidity(question.id)
              submitField(index, question.id)
            }"
          />
        </div>

        <div
          v-if="question.data_type?.value === QST.INPUT.TYPE.DROPDOWN"
          :class="{
            'text-disabled pointer-none': !question.visibility || presenceIsFalse,
            'd-none': !showHiddenQuestions && !question.visibility
          }"
        >
          <div class="text-body-1 font-weight-bold">
            {{ question.label }} {{ question.mandatory && '*' || '' }}
            <v-progress-circular
                v-if="questionLoadingState[index]"
                size="x-small"
                indeterminate
            />
            <history-value
                :question="question"
                :missionReadingHistory="missionReadingHistory"
                :attachment-type="props.attachmentType"
                :attachment-id="props.attachmentId" />
          </div>

          <v-select
            v-model="formData[question.id]"
            :items="question.value && question.value.split(',').map(item => item.trim()) || []"
            density="compact"
            single-line
            :rules="inputRules(question)"
            :return-object="true"
            :value="question.default_value"
            @update:model-value="() => {
              questionLoadingState[index] = true;
              formIsValid[question.id] = null;
              submitField(index, question.id)
            }"
          />
        </div>

        <div
          v-if="question.data_type?.value === QST.INPUT.TYPE.DATE"
          :class="{
            'text-disabled pointer-none': !question.visibility || presenceIsFalse,
            'd-none': !showHiddenQuestions && !question.visibility
          }"
        >
          <div class="text-body-1 font-weight-bold">
            {{ question.label }} {{ question.mandatory && '*' || '' }}
            <v-progress-circular
                v-if="questionLoadingState[index]"
                size="x-small"
                indeterminate
            />
            <history-value
                :question="question"
                :missionReadingHistory="missionReadingHistory"
                :attachment-type="props.attachmentType"
                :attachment-id="props.attachmentId" />
          </div>

          <date-picker-input-old
            v-model="formData[question.id]"
            :rules="inputRules(question)"
            :to-iso="true"
            v-bind="getQuestionInformations(question, formData[question.id])"
            @update:model-value="() => {
              questionLoadingState[index] = true;
              formIsValid[question.id] = null;
              submitField(index, question.id)
            }"
          />
        </div>
      </v-form>
    </div>

    <div class="my-4 float-right">
      <v-row align="center">
        <v-col cols="auto">
          <v-btn
            v-if="existQuestionsWithHistory"
            variant="tonal"
            :prepend-icon="mdiHistory"
            color="info"
            @click="completeValuesFromHistory()"
          >
            {{ $t('mission-planning.qst.my-questionnaires.qst.btn.history-recovery') }}
          </v-btn>
        </v-col>
      </v-row>
    </div>
  </v-container>
  <v-snackbar
      v-model="snackbar"
      :timeout="3000"
      variant="elevated"
  >
    <div class="d-flex justify-center">
    {{ $t('mission-planning.qst.my-questionnaires.qst.history-recovery-notification', {answerCount: insertedAnswersFromHistoryCount}) }}
    </div>
  </v-snackbar>
</template>

<script setup>
import { mdiHistory } from '@mdi/js';
import {ref, reactive, computed, inject, watch, toRaw} from 'vue';
import { useI18n } from 'vue-i18n';
import { extractFormNamesFromQuestions } from '@/stores/manager/configurationManager';
import { useStoreManager } from '@/stores/storeManager';
import { useApplicationStore } from '@/stores/applicationStore';
import { READING, QST, VALUE } from '@/constants';
import {
  extractReadingIdByParams,
  extractReadingAnswersByReadingId,
  extractReadingAnswersByQuestionIdAndAttachment,
  createReadingIfNotExists
} from '@/stores/manager/readingManager';
import {
  extractQuestionsIds,
  checkIfExistsSomeQuestionsWithHistory
} from '@/stores/manager/configurationManager';
import HistoryValue from './HistoryValue.vue';
import dateHandler from '@/utils/dateHandler';
import _ from 'lodash';
import { saveReadingCountersDebounced, clearInvalidAnswers } from '@/stores/manager/conditionsManager';
import { ConditionsController } from "@/utils/conditions/conditionsController";
import { QuestionnaireConditionChecker } from '@/utils/conditions/conditionChecker/QuestionnaireConditionChecker';
import ConditionDebugAll from "@/components/common/ConditionDebugAll.vue";
import {v4 as uuidv4} from "uuid";
import questionsHandler from "@/utils/questionsHandler";
import YesNoToggleInputOld from '@/components/common/YesNoToggleInputOld.vue'
import DatePickerInputOld from '@/components/common/DatePickerInputOld.vue'

const { t } = useI18n();

const props = defineProps({
  questions: {
    type: Object,
    required: true
  },
  attachmentType: {
    type: String,
    required: false
  },
  attachmentId: {
    type: [Number, String],
    required: false
  },
});

const storeManager = useStoreManager();
const currentMissionPlanningId = inject('currentMissionPlanningId');
const currentConfiguration = inject('currentConfiguration');
const currentPointOfSale = inject('currentPointOfSale');
const categoryHasSwitched = inject('categoryHasSwitched');
const originalReading = inject('originalReading');
const snackbar = ref(false);
const insertedAnswersFromHistoryCount = ref(0);
const questionsCopy = ref(JSON.parse(JSON.stringify(props.questions)));
const formIsValid = ref({})
const initialState = ref(true);
const yesNoInput = ref({});
const formRef = ref({});

const applicationStore = useApplicationStore();
const userDetails = computed(() => applicationStore.userDetails);
const showHiddenQuestions = computed(() => applicationStore.showHiddenQuestions);

const questionLoadingState = reactive([]);

const missionPlanning = computed(() =>
  storeManager.getFromStoreByCategory('mission', 'missionsPlannings')[currentMissionPlanningId.value] || {}
);

const mission = computed(() =>
  storeManager.getFromStoreByCategory('mission', 'missions')[missionPlanning.value?.mission?.id] || {}
);

const readingsHistory = computed(() =>
  storeManager.getFromStoreByCategory('readingHistory', 'reading')
);

const readings = computed(() =>
  storeManager.getFromStoreByCategory('reading', 'reading') || {}
);

const readingAnswers = computed(() =>
  storeManager.getFromStoreByCategory('reading', 'answer') || {}
);

const missionReadingHistory = computed(() => {
  return Object.values(readingsHistory.value).find((rh) => parseInt(rh.mission) === parseInt(missionPlanning.value?.mission?.id))
});

const { foundAnswers } = getDataFound();

const currentReadingId = computed(() =>
    extractReadingIdByParams(
        readings.value,
        mission.value?.configuration?.id,
        currentMissionPlanningId.value,
        userDetails.value?.id
    )
);

const checkAndUpdateFormValidity = async (q) => {
  const isValid = await validateForm(q);
  formIsValid.value[q] = isValid;
}

const validateForm = async (q) => {
  let valid = false;

  const yesNoValid = yesNoInput.value[q].validate();
  if (yesNoValid) valid = true;

  return valid;
};

const answers = computed(
    () =>
        Object.values(storeManager.getFromStoreByCategory('reading', 'answer'))
            .filter(answer => answer.reading?.toString() === currentReadingId.value?.toString())
        || {});


const formData = ref(
  extractFormNamesFromQuestions(
    questionsCopy.value,
    foundAnswers,
    props.attachmentType,
    props.attachmentId
  )
);

const existQuestionsWithHistory =
  checkIfExistsSomeQuestionsWithHistory(
    questionsCopy.value,
    missionReadingHistory?.value,
    props.attachmentType,
    props.attachmentId
  );

function getDataFound() {
  const foundReadingId =
    extractReadingIdByParams(
      readings.value,
      mission.value?.configuration?.id,
      currentMissionPlanningId.value,
      userDetails.value?.id
    );

  const foundAnswers =
    extractReadingAnswersByReadingId(
      readingAnswers.value,
      foundReadingId,
      extractQuestionsIds(questionsCopy.value)
    );

  return {
    foundReadingId,
    foundAnswers
  }
}

const currentAnswers = computed(() =>
    extractReadingAnswersByReadingId(
        readingAnswers.value,
        currentReadingId.value,
        extractQuestionsIds(questionsCopy.value)
    )
);

function addNewReadingAnswer(questionId, readingId, creationDate, valid) {
  const readingAnswer = {
    reading: readingId,
    question: parseInt(questionId),
    value: valid ? formData.value[questionId] : null,
    attachment_type: props.attachmentType || READING.ATTACHMENT_TYPE.NONE,
    attachment_id: props.attachmentId || null,
    creation_date: creationDate
  };
  return storeManager.add('reading', 'answer', readingAnswer);
}

let debounceSubmit = _.debounce(submitField, 300);

const setLocalReadingAnswers = (readingId) => {
  return Object.keys(toRaw(readingAnswers.value)).reduce((acc, raId) => {
    if (readingAnswers.value[raId].reading === readingId) {
      acc[raId] = toRaw(readingAnswers.value[raId])
    }
    return acc
  }, {})
}
const addNewAnswerToLocalReadingAnswers = (
    questionId,
    readingId,
    creationDate,
    localReadingAnswers,
    valid
) => {
  const nId = uuidv4()
  return {
    ...localReadingAnswers,
    [nId]: {
      reading: readingId,
      question: parseInt(questionId),
      value: valid ? formData.value[questionId] : null,
      attachment_type: props.attachmentType || READING.ATTACHMENT_TYPE.NONE,
      attachment_id: props.attachmentId || null,
      creation_date: creationDate
    }
  }
}
function submitField(index, questionId) {
  initialState.value = false;
  const unWatch = watch(
      () => formIsValid.value[questionId],
      (valid) => {
        if (valid === null) return;
        setTimeout(() => {
          requestAnimationFrame(async () => {
            await dispatchSubmitField({
              valid: valid,
              index: index,
              questionId: questionId
            })
            unWatch()
          });
        }, 0)
      },
      { immediate: true }
  )
}
async function dispatchSubmitField({valid, index, questionId}) {
  let localReadingAnswers = {}
  let readingId;
  const creationDate = dateHandler.generateNowUnixTimestamp();
  const { foundAnswers, foundReadingId } = getDataFound();
  if (!foundReadingId) {
    readingId = await createReadingIfNotExists(
        '',
        mission.value?.configuration?.id,
        currentMissionPlanningId.value || null,
        userDetails.value.id,
        currentConfiguration.value?.data || null
    );
  } else {
    readingId = foundReadingId;
  }
  localReadingAnswers = setLocalReadingAnswers(readingId)
  const foundAnswerForQuestion = await extractReadingAnswersByQuestionIdAndAttachment(
      foundAnswers,
      parseInt(questionId),
      props.attachmentType,
      props.attachmentId
  );
  if (_.isEmpty(foundAnswerForQuestion)) {
    setTimeout(() => {
      addNewReadingAnswer(questionId, readingId, creationDate, valid)
    }, 0)
    localReadingAnswers = addNewAnswerToLocalReadingAnswers(
        questionId,
        readingId,
        creationDate,
        localReadingAnswers,
        valid
    )
  } else {
    const clonedFoundAnswerForQuestion = {
      ...foundAnswerForQuestion.obj
    };
    clonedFoundAnswerForQuestion.value = valid ? formData.value[questionId] : null;
    setTimeout(() => {
      storeManager.update('reading', 'answer', foundAnswerForQuestion.key, clonedFoundAnswerForQuestion);
    }, 0)
    localReadingAnswers[foundAnswerForQuestion.key].value = valid ? formData.value[questionId] : null
  }
  launchUpdateConditions(readingId, index, questionId, localReadingAnswers);
}

async function addReadingAnswerFromHistory(questionId, readingId, answerValue, attachmentType, attachmentId) {
  const creationDate = dateHandler.generateNowUnixTimestamp();
  const readingAnswer = {
    reading: readingId,
    question: parseInt(questionId),
    value: answerValue,
    attachment_type: attachmentType,
    attachment_id: attachmentId,
    creation_date: creationDate
  };
  return await storeManager.add('reading', 'answer', readingAnswer);
}

function triggerFormsReload() {
  formData.value =
      extractFormNamesFromQuestions(
          questionsCopy.value,
          currentAnswers.value,
          props.attachmentType,
          props.attachmentId
      )
  ;
}

const recursiveClearInvalidAnswers = async (readingId) => {
  launchCreateConditions(readingId);
  if (await clearInvalidAnswers(readingId)) {
    await recursiveClearInvalidAnswers(readingId);
  }
}

function countInsertedAnswers(insertedAnswers) {
  // TODO: reactivate precise counting if needed but optimize performance
  // const insertedSet = new Set(insertedAnswers);
  //
  // const allAnswers = storeManager.getFromStoreByCategory('reading', 'answer');
  // let count = 0;
  //
  // for (const [key, answer] of Object.entries(allAnswers)) {
  //   if (
  //       answer.reading?.toString() === currentReadingId.value?.toString() &&
  //       insertedSet.has(key)
  //   ) {
  //     count++;
  //   }
  // }
  // return count;
  return insertedAnswers.length;
}

async function completeValuesFromHistory() {
  insertedAnswersFromHistoryCount.value = 0;
  const answersHistory = missionReadingHistory.value?.answers;

  if (!answersHistory) {
    return;
  }

  if (!currentReadingId.value || currentReadingId.value === VALUE.DEFAULT) {
    await createReadingIfNotExists(
        currentReadingId.value,
        mission.value?.configuration?.id,
        currentMissionPlanningId.value || null,
        userDetails.value.id,
        currentConfiguration.value?.data || null
    );
  }

  const questionMap = new Map(
      questionsCopy.value.map((q) => [q.id?.toString(), q])
  );

  const answersByQuestion = new Map();
  for (const a of answers.value) {
    if (a.value === null || a.value === undefined) continue;
    const qId = a.question?.toString();
    if (!qId) continue;
    let list = answersByQuestion.get(qId);
    if (!list) {
      list = [];
      answersByQuestion.set(qId, list);
    }
    list.push(a);
  }

  const currentGroupAnswersHistory = Object.values(answersHistory).filter(
      (ah) =>
          (ah.related_to_type === props.attachmentType || ah.related_to_type === READING.ATTACHMENT_TYPE.NONE) &&
          (ah.related_to_id === props.attachmentId || !ah.related_to_id) && questionMap.has(ah.question_id?.toString())
  );

  const insertions = currentGroupAnswersHistory.map(async (answerHistory) => {
    const questionId = answerHistory.question_id?.toString();
    const relatedId = answerHistory.related_to_id ?? null;
    const type = answerHistory.related_to_type;

    const question = questionMap.get(questionId);
    if (!question || !question.history) {
      return null;
    }

    const existingForQuestion = answersByQuestion.get(questionId) || [];

    if (type === READING.ATTACHMENT_TYPE.NONE) {
      if (existingForQuestion.length > 0) {
        return null;
      }
    } else {
      if (existingForQuestion.some(a =>
          a.attachment_type === type &&
          a.attachment_id?.toString() === relatedId?.toString()
      )) {
        return null;
      }
    }

    return await addReadingAnswerFromHistory(
        answerHistory.question_id,
        currentReadingId.value,
        answerHistory.value,
        type,
        relatedId
    );
  });

  const insertedAnswersRaw = await Promise.all(insertions);
  const insertedAnswers = insertedAnswersRaw.filter(Boolean);

  await recursiveClearInvalidAnswers(currentReadingId.value);
  triggerFormsReload();

  insertedAnswersFromHistoryCount.value = countInsertedAnswers(insertedAnswers);
  if (insertedAnswersFromHistoryCount.value > 0) snackbar.value = true;
}

function inputRules(qst) {
  return [
    (v) => qst.mandatory ? (!!v || v === false || t('global.validator.mandatoryField')) : true,
    (v) => conditionCheckerRule(qst, v)
  ];
}

function updateQuestionConditionsFromCalculationResult(questionsConditionsResult) {
  for (let q of questionsCopy.value) {
    const builtId = questionsHandler.buildId(q.id, props.attachmentType, props.attachmentId)
    Object.assign(q, questionsConditionsResult[builtId]);
  }

}
function launchUpdateConditions(readingId, index, questionId, localReadingAnswers ) {
  let initialContext = {
    currentValue: null,
    answers: Object.values(localReadingAnswers),
    pos: currentPointOfSale.value,
    attachementType: props.attachmentType || READING.ATTACHMENT_TYPE.NONE,
    attachementId: props.attachmentId,
    prod: null,
    nowDatetime: new Date(),
    currentQuestionId: questionId
  }
  const conditionCalculatorInstance = new ConditionsController(
      currentConfiguration.value,
      initialContext,
      readingId
  );
  if (props.attachmentType === READING.ATTACHMENT_TYPE.PICTURE) {
    const { questions } = conditionCalculatorInstance.createConditions();
    updateQuestionConditionsFromCalculationResult(questions)
  } else {
    const { questions } = conditionCalculatorInstance.updateConditions();
    updateQuestionConditionsFromCalculationResult(questions)
  }
  saveReadingCountersDebounced(readingId, currentConfiguration.value)
  if (index !== null) questionLoadingState[index] = false;
}

function launchCreateConditions(readingId, index = null ) {
  let initialContext = {
    currentValue: null,
    answers: Object.values(readingAnswers.value).filter((ra) => ra.reading === readingId),
    pos: currentPointOfSale.value,
    attachementType: 'none',
    attachementId: null,
    prod: null,
    nowDatetime: new Date()
  }
  const { questions } = new ConditionsController(
      currentConfiguration.value,
      initialContext,
      readingId
  ).createConditions();
  updateQuestionConditionsFromCalculationResult(questions)
  saveReadingCountersDebounced(readingId, currentConfiguration.value)
  if (index !== null) questionLoadingState[index] = false;
}

async function conditionCheckerRule(question, value) {
  return new Promise(resolve => {
    const questionnaireConditionChecker = new QuestionnaireConditionChecker()
    const context = buildContext(value)
    const questionValidityCondition = question.conditions['validity'];
    const validationResult = questionnaireConditionChecker.check(questionValidityCondition, context)
    return resolve(validationResult?.result === true || validationResult?.messages?.join(t('conditions-messages.or')));
  })
}

function getQuestionInformations(question, value) {
  const questionnaireConditionChecker = new QuestionnaireConditionChecker()
  const context = buildContext(value)
  const questionValidityCondition = question.conditions['validity'];
  return questionnaireConditionChecker.getInformations(questionValidityCondition, context)
}

function buildContext(value) {
  const products = currentConfiguration.value?.catalog?.product || []
  return {
    currentValue: value,
    answers: Object.values(readingAnswers.value).filter((ra) => ra.reading === currentReadingId.value),
    pos: currentPointOfSale.value,
    attachementType: props.attachmentType,
    attachementId: props.attachmentId,
    prod: props.attachmentType === READING.ATTACHMENT_TYPE.PRODUCT ? products[props.attachmentId] : null,
    nowDatetime: new Date()
  }
}

const presenceIsFalse = computed(() => {
  return !originalReading.value?.data?.isPresentDate && currentConfiguration.value?.settings?.promoter_geolocation
})

watch(
    () => questionsCopy.value,
    (newQuestions) => {
      newQuestions.forEach((question) => {
        if (!(question.id in formIsValid.value)) {
          formIsValid.value[question.id] = null
        }
      })
    },
    { deep: true, immediate: true, once: true }
)

watch(
    () => props.questions,
    (newQuestions) => {
      if (!initialState.value) {
        return;
      }
      questionsCopy.value = JSON.parse(JSON.stringify(newQuestions));
      triggerFormsReload()
    },
    { deep: true, immediate: true }
)

watch(
  () => categoryHasSwitched.value,
  () => {
    initialState.value = true;
  }
)

</script>
